import { createMongoAbility } from '@casl/ability';
import * as React from 'react';
import { createContext, useContext } from 'react';
import storage from '../../hooks/useStorage/useStorage';
import { useGetAbilities } from '../../services/modules/users/users.queries';
import { useNetworkContext } from '../offline/contexts/network/network.context';
import {
  Ability,
  AbilityContextProviderProps,
  RoleBehaviorCapability,
  UserTeamConditions,
} from './ability.types';
import getAbility from './ability.utils';

const DEFAULT_VALUE = createMongoAbility([]);

export const AbilityContext = createContext<Ability>({
  can: () => false,
  cannot: () => false,
  getAbilities: async () => {
    return;
  },
  getConditions: () => undefined,
  hasCapability: () => false,
  getUserContextGroupId: () => undefined,
  isCustomRole: () => false,
  resetAbilities: () => {
    return;
  },
  getAbilityApi: () => DEFAULT_VALUE,
  isLoadingAbilities: false,
});

export const AbilityContextProvider = ({ children }: AbilityContextProviderProps) => {
  const [ability, setAbility] = React.useState(DEFAULT_VALUE);
  const [conditions, setConditions] = React.useState<UserTeamConditions | undefined>(undefined);
  const [capabilities, setCapabilities] = React.useState<RoleBehaviorCapability[]>([]);
  const [userContextGroupId, setUserContextGroupId] = React.useState<string | undefined>(undefined);
  const [customRole, setCustomRole] = React.useState<boolean>(false);
  const [hasSyncedAbilities, setHasSyncedAbilities] = React.useState(false);
  const teamId = storage.getTeamId();
  const { isOnline } = useNetworkContext();
  const isAbilitiesQueryEnabled = !!teamId && isOnline;

  const {
    refetch,
    data: abilityFromApi,
    isFetched: isAbilitiesFetched,
  } = useGetAbilities({
    teamId,
    enabled: isAbilitiesQueryEnabled,
  });

  const resetAbilities = React.useCallback(() => {
    setAbility(DEFAULT_VALUE);
    setCustomRole(false);
    setConditions(undefined);
    setCapabilities([]);
    setUserContextGroupId(undefined);
    setHasSyncedAbilities(false);
  }, []);

  const getAbilities = React.useCallback(async () => {
    await refetch();
  }, []);

  React.useEffect(() => {
    if (abilityFromApi?.data) {
      setAbility(getAbility(abilityFromApi.data.abilities));
      setConditions(abilityFromApi.data.conditions);
      setCapabilities(abilityFromApi.data.behaviorSettings?.capabilities ?? []);
      setUserContextGroupId(abilityFromApi.data.userContextGroupId);
      setCustomRole(abilityFromApi.data.customRole);
      setHasSyncedAbilities(true);
    } else if (isAbilitiesFetched) {
      setHasSyncedAbilities(true);
    }
  }, [abilityFromApi, isAbilitiesFetched]);

  const value: Ability = {
    can: (action, subject) => ability.can(action, subject),
    cannot: (action, subject) => ability.cannot(action, subject),
    getAbilities,
    getConditions: () => conditions,
    hasCapability: (capability) => capabilities.includes(capability),
    getUserContextGroupId: () => userContextGroupId,
    isCustomRole: () => customRole,
    resetAbilities,
    getAbilityApi: () => ability,
    isLoadingAbilities: isAbilitiesQueryEnabled && !hasSyncedAbilities,
  };

  return <AbilityContext.Provider value={value}>{children}</AbilityContext.Provider>;
};

export const useAbility = () => useContext<Ability>(AbilityContext);
